import { useLoader } from '@react-three/fiber';
import { useMemo } from 'react';
import { Color, Group, Mesh, MeshStandardMaterial } from 'three';
import { MTLLoader, OBJLoader } from 'three-stdlib';

/**
 * Stylized fallback colours for the Quaternius "Stylized Nature MegaKit" pack.
 * The pack's MTL files define textures via broken absolute Windows paths
 * (`map_Kd C:/Rocks_Diffuse.png`) AND don't define a `Kd` diffuse colour,
 * so without an override every nature prop renders pure white.
 *
 * We match by the material name set in the MTL (e.g. `Rocks`, `Bark_DeadTree`)
 * and assign a stylised solid colour. Textures are NOT loaded — flat shading
 * is fine for this art style and keeps GPU memory low on mobile.
 */
const MATERIAL_NAME_COLOR_OVERRIDES: Record<string, string> = {
  // Rocks / pebbles
  Rocks:               '#7d8794',
  PathRocks:           '#6a7280',
  Rocks_Desert:        '#a48662',
  // Trees
  Bark_NormalTree:     '#5a3b22',
  Bark_DeadTree:       '#7a614a',
  Bark_TwistedTree:    '#4a3422',
  Leaves_NormalTree:   '#3a8a48',
  Leaves_TwistedTree:  '#2c6a38',
  Leaves_DeadTree:     '#7d6a40',
  Leaf_Pine:           '#2c5e3a',
  Leaves_GiantPine:    '#235033',
  // Ground covers
  Mushrooms:           '#c25a4a',
  Grass:               '#5aa848',
  Flowers:             '#e07aa6',
  Fern:                '#3e8540',
  Plants:              '#4a8a40',
  Petals:              '#f0a0c0',
  Clover:              '#52a85a',
};

/**
 * Loads an .obj mesh together with its sibling .mtl. Both files must be present
 * (Quaternius weapon/helmet packs always ship both). Throws to Suspense on miss
 * so the parent ErrorBoundary can show the placeholder.
 *
 * MTL Phong materials get upgraded to MeshStandardMaterial so they shade nicely
 * under the scene's directional + ambient lights. If the MTL omits its diffuse
 * colour AND the material name matches a known nature/prop name, we apply a
 * stylised fallback colour so the mesh isn't pure white.
 */
export function useObjMesh(objPath: string, tintColor?: string): Group {
  const mtlPath = objPath.replace(/\.obj$/i, '.mtl');
  const materials = useLoader(MTLLoader, mtlPath);
  const obj = useLoader(OBJLoader, objPath, (loader) => {
    materials.preload();
    (loader as OBJLoader).setMaterials(materials);
  });

  return useMemo(() => {
    const cloned = obj.clone(true) as Group;
    const tint = tintColor ? new Color(tintColor) : null;
    cloned.traverse((child) => {
      if (!(child as Mesh).isMesh) return;
      const mesh = child as Mesh;
      mesh.castShadow = true;
      const upgrade = (m: MeshStandardMaterial): MeshStandardMaterial => {
        const std = new MeshStandardMaterial();
        if ('color' in m) std.color.copy(m.color);
        std.metalness = 0.5;
        std.roughness = 0.55;

        // If the material is essentially white (MTL had no Kd, only broken
        // texture refs) and its name matches a known nature material, apply
        // the stylised fallback colour.
        const isWhite = std.color.r > 0.97 && std.color.g > 0.97 && std.color.b > 0.97;
        const fallback = m.name && MATERIAL_NAME_COLOR_OVERRIDES[m.name];
        if (isWhite && fallback) {
          std.color.set(fallback);
        }

        if (tint) std.color.copy(tint);
        return std;
      };
      const orig = mesh.material as MeshStandardMaterial | MeshStandardMaterial[];
      mesh.material = Array.isArray(orig) ? orig.map(upgrade) : upgrade(orig);
    });
    return cloned;
  }, [obj, tintColor]);
}
